import React, { useMemo, useState } from "react";
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaPlus,
  FaSpinner,
  FaTimesCircle,
  FaTrash,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { apiUrl } from "../../globalVariables";

const DEFAULT_QUESTION =
  "What is the verified qualifying monthly income breakdown for this loan?";

const EMPTY_JOB = {
  organizationId: "",
  loanId: "",
  question: DEFAULT_QUESTION,
};

const DEFAULT_ENDPOINT = `${apiUrl}/admin/income-task-mismatch-eval`;

function createJob(overrides = {}) {
  return {
    ...EMPTY_JOB,
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    ...overrides,
  };
}

function normalizeResults(data, fallbackJobs) {
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data?.results)) return data.data.results;
  if (Array.isArray(data?.evaluations)) return data.evaluations;
  if (Array.isArray(data?.data?.evaluations)) return data.data.evaluations;

  if (data?.result) return [data.result];

  return fallbackJobs.map((job) => ({
    organizationId: job.organizationId,
    loanId: job.loanId,
    status: data?.success ? "completed" : "error",
    verdict: data?.success ? "unknown" : "error",
    reason: data?.reason || data?.message || "Endpoint did not return row-level results.",
    raw: data,
  }));
}

function getVerdict(result) {
  return String(
    result?.verdict ||
      result?.judge?.verdict ||
      result?.status ||
      result?.outcome ||
      "unknown"
  ).toLowerCase();
}

function verdictConfig(result) {
  const verdict = getVerdict(result);
  if (["pass", "passed", "match", "matched", "success", "ok"].includes(verdict)) {
    return { label: "Pass", className: "bg-success", icon: FaCheckCircle };
  }

  if (["fail", "failed", "mismatch", "drift", "issue"].includes(verdict)) {
    return { label: "Fail", className: "bg-danger", icon: FaTimesCircle };
  }

  if (["warning", "warn", "review", "needs_review", "unknown"].includes(verdict)) {
    return { label: "Review", className: "bg-warning text-dark", icon: FaExclamationTriangle };
  }

  return { label: verdict || "Unknown", className: "bg-secondary", icon: FaExclamationTriangle };
}

function getChecklistTotal(result) {
  return (
    result?.checklistTotal ||
    result?.checklist?.totalQualifyingMonthlyIncome ||
    result?.checklist?.total ||
    result?.expectedTotal ||
    "-"
  );
}

function getAskAddyTotal(result) {
  return (
    result?.askAddyTotal ||
    result?.askAddy?.totalQualifyingMonthlyIncome ||
    result?.askAddy?.total ||
    result?.actualTotal ||
    "-"
  );
}

function getReason(result) {
  return (
    result?.reason ||
    result?.judge?.reason ||
    result?.judge?.comment ||
    result?.summary ||
    result?.message ||
    "-"
  );
}

function parseBulkRows(text, defaultQuestion) {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [organizationId, loanId, ...questionParts] = line
        .split(/[,\t]/)
        .map((part) => part.trim());

      return createJob({
        organizationId: organizationId || "",
        loanId: loanId || "",
        question: questionParts.join(", ") || defaultQuestion,
      });
    })
    .filter((job) => job.organizationId || job.loanId);
}

function VerdictBadge({ result }) {
  const config = verdictConfig(result);
  const Icon = config.icon;

  return (
    <span className={`badge ${config.className}`}>
      <Icon className="me-1" />
      {config.label}
    </span>
  );
}

function IncomeMismatchEvalPage() {
  const [jobs, setJobs] = useState([createJob()]);
  const [bulkInput, setBulkInput] = useState("");
  const [endpoint, setEndpoint] = useState(DEFAULT_ENDPOINT);
  const [useLlmJudge, setUseLlmJudge] = useState(true);
  const [includeLiveAskAddy, setIncludeLiveAskAddy] = useState(true);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState([]);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const validJobs = useMemo(
    () =>
      jobs
        .map((job) => ({
          ...job,
          organizationId: job.organizationId.trim(),
          loanId: job.loanId.trim(),
          question: (job.question || DEFAULT_QUESTION).trim(),
        }))
        .filter((job) => job.organizationId && job.loanId),
    [jobs]
  );

  const summary = useMemo(() => {
    const total = results.length;
    const failures = results.filter((result) =>
      ["fail", "failed", "mismatch", "drift", "issue"].includes(getVerdict(result))
    ).length;
    const passes = results.filter((result) =>
      ["pass", "passed", "match", "matched", "success", "ok"].includes(getVerdict(result))
    ).length;
    const review = total - failures - passes;

    return { total, failures, passes, review };
  }, [results]);

  const addJob = () => setJobs((prev) => [...prev, createJob()]);

  const removeJob = (id) => {
    setJobs((prev) => prev.filter((job) => job.id !== id));
  };

  const updateJob = (id, field, value) => {
    setJobs((prev) =>
      prev.map((job) => (job.id === id ? { ...job, [field]: value } : job))
    );
  };

  const applyBulkRows = () => {
    const parsed = parseBulkRows(bulkInput, DEFAULT_QUESTION);
    if (parsed.length === 0) {
      toast.warning("Paste rows as orgId,loanId or orgId,loanId,question.");
      return;
    }

    setJobs(parsed);
    toast.success(`Loaded ${parsed.length} eval row(s).`);
  };

  const runEval = async () => {
    if (validJobs.length === 0) {
      toast.error("Add at least one row with both organization ID and loan ID.");
      return;
    }

    if (!endpoint.trim()) {
      toast.error("Endpoint URL is required.");
      return;
    }

    setRunning(true);
    setResults([]);

    try {
      const response = await fetch(endpoint.trim(), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          loans: validJobs.map(({ organizationId, loanId, question }) => ({
            organizationId,
            loanId,
            question,
          })),
          options: {
            useLlmJudge,
            includeLiveAskAddy,
            topic: "income",
          },
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success === false) {
        throw new Error(data.reason || data.message || `HTTP ${response.status}`);
      }

      const normalized = normalizeResults(data, validJobs);
      setResults(normalized);
      toast.success(`Evaluated ${normalized.length} loan(s).`);
    } catch (error) {
      toast.error(`Income eval failed: ${error.message}`);
      setResults(
        validJobs.map((job) => ({
          organizationId: job.organizationId,
          loanId: job.loanId,
          verdict: "error",
          reason: error.message,
        }))
      );
    } finally {
      setRunning(false);
    }
  };

  const toggleExpanded = (rowKey) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) {
        next.delete(rowKey);
      } else {
        next.add(rowKey);
      }
      return next;
    });
  };

  return (
    <div className="container-fluid py-4">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h1 className="mb-1" style={{ fontSize: "1.7rem", fontWeight: 700 }}>
            Income Task Mismatch Eval
          </h1>
          <p className="text-muted mb-0">
            Batch loan/org IDs, compare Ask Addy income answers against the finalized
            Processing Checklist income task, and use an LLM judge to flag drift.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={runEval}
          disabled={running || validJobs.length === 0}
          style={{ background: "#6366f1", borderColor: "#6366f1" }}
        >
          {running ? (
            <>
              <FaSpinner className="me-2 fa-spin" />
              Running eval...
            </>
          ) : (
            `Run ${validJobs.length || jobs.length} eval(s)`
          )}
        </button>
      </div>

      <div className="alert alert-info">
        <strong>Expected backend contract:</strong> POST rows to an admin endpoint with
        <code className="mx-1">loans: [{`{ organizationId, loanId, question }`}]</code>.
        The backend should fetch the Processing Checklist income task first, compare it
        with Ask Addy's answer, and return row-level judge verdicts.
      </div>

      <div className="card mb-4">
        <div className="card-header bg-white">
          <span className="fw-semibold">Endpoint and judge options</span>
        </div>
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-lg-8">
              <label className="form-label small text-muted">Admin endpoint</label>
              <input
                type="text"
                className="form-control font-monospace"
                value={endpoint}
                onChange={(event) => setEndpoint(event.target.value)}
                disabled={running}
              />
            </div>
            <div className="col-lg-4">
              <div className="form-check">
                <input
                  id="useLlmJudge"
                  className="form-check-input"
                  type="checkbox"
                  checked={useLlmJudge}
                  onChange={(event) => setUseLlmJudge(event.target.checked)}
                  disabled={running}
                />
                <label className="form-check-label" htmlFor="useLlmJudge">
                  Use LLM judge
                </label>
              </div>
              <div className="form-check">
                <input
                  id="includeLiveAskAddy"
                  className="form-check-input"
                  type="checkbox"
                  checked={includeLiveAskAddy}
                  onChange={(event) => setIncludeLiveAskAddy(event.target.checked)}
                  disabled={running}
                />
                <label className="form-check-label" htmlFor="includeLiveAskAddy">
                  Include live Ask Addy answer
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header bg-white d-flex justify-content-between align-items-center">
          <span className="fw-semibold">Loans to evaluate</span>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={addJob}
            disabled={running}
          >
            <FaPlus className="me-1" />
            Add row
          </button>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th className="ps-3" style={{ width: "3rem" }}>#</th>
                  <th>Organization ID</th>
                  <th>Loan ID</th>
                  <th>Question</th>
                  <th style={{ width: "4rem" }} />
                </tr>
              </thead>
              <tbody>
                {jobs.map((job, index) => (
                  <tr key={job.id}>
                    <td className="ps-3 text-muted small">{index + 1}</td>
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm font-monospace"
                        placeholder="organizationId"
                        value={job.organizationId}
                        onChange={(event) =>
                          updateJob(job.id, "organizationId", event.target.value)
                        }
                        disabled={running}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm font-monospace"
                        placeholder="loanId"
                        value={job.loanId}
                        onChange={(event) => updateJob(job.id, "loanId", event.target.value)}
                        disabled={running}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder={DEFAULT_QUESTION}
                        value={job.question}
                        onChange={(event) => updateJob(job.id, "question", event.target.value)}
                        disabled={running}
                      />
                    </td>
                    <td>
                      {jobs.length > 1 && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => removeJob(job.id)}
                          disabled={running}
                          title="Remove row"
                        >
                          <FaTrash />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header bg-white">
          <span className="fw-semibold">Bulk paste</span>
          <span className="text-muted small ms-2">orgId,loanId or orgId,loanId,question</span>
        </div>
        <div className="card-body">
          <textarea
            className="form-control font-monospace"
            rows={4}
            placeholder={`org_123,loan_abc\norg_456,loan_def,What is the income breakdown?`}
            value={bulkInput}
            onChange={(event) => setBulkInput(event.target.value)}
            disabled={running}
          />
          <div className="d-flex justify-content-end mt-3">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={applyBulkRows}
              disabled={running || !bulkInput.trim()}
            >
              Replace table with pasted rows
            </button>
          </div>
        </div>
      </div>

      {results.length > 0 && (
        <>
          <div className="row g-3 mb-3">
            <div className="col-md-3">
              <SummaryCard label="Total" value={summary.total} />
            </div>
            <div className="col-md-3">
              <SummaryCard label="Pass" value={summary.passes} color="#16a34a" />
            </div>
            <div className="col-md-3">
              <SummaryCard label="Fail" value={summary.failures} color="#dc2626" />
            </div>
            <div className="col-md-3">
              <SummaryCard label="Review" value={summary.review} color="#d97706" />
            </div>
          </div>

          <div className="card">
            <div className="card-header bg-white">
              <span className="fw-semibold">Judge results</span>
            </div>
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="ps-3">Loan</th>
                    <th>Org</th>
                    <th>Verdict</th>
                    <th>Checklist total</th>
                    <th>Ask Addy total</th>
                    <th>Reason</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((result, index) => {
                    const rowKey = `${result.organizationId || result.orgId}-${result.loanId}-${index}`;
                    const isExpanded = expandedRows.has(rowKey);

                    return (
                      <React.Fragment key={rowKey}>
                        <tr>
                          <td className="ps-3 font-monospace small">
                            {result.loanId || "-"}
                          </td>
                          <td className="font-monospace small text-muted">
                            {result.organizationId || result.orgId || "-"}
                          </td>
                          <td>
                            <VerdictBadge result={result} />
                          </td>
                          <td className="font-monospace small">{getChecklistTotal(result)}</td>
                          <td className="font-monospace small">{getAskAddyTotal(result)}</td>
                          <td style={{ maxWidth: 420 }}>
                            <span className="small">{getReason(result)}</span>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => toggleExpanded(rowKey)}
                            >
                              {isExpanded ? "Hide" : "View"}
                            </button>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={7} className="bg-light">
                              <pre className="mb-0 small" style={{ whiteSpace: "pre-wrap" }}>
                                {JSON.stringify(result, null, 2)}
                              </pre>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SummaryCard({ label, value, color = "#4f46e5" }) {
  return (
    <div className="card text-center h-100">
      <div className="card-body py-3">
        <div className="text-muted small text-uppercase fw-semibold">{label}</div>
        <div className="fs-3 fw-bold" style={{ color }}>
          {value}
        </div>
      </div>
    </div>
  );
}

export default IncomeMismatchEvalPage;
